import { Tick02Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { DropdownMenu as MenuPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;

export const DropdownMenuContent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof MenuPrimitive.Content>>(
  function DropdownMenuContent({ className, align = 'end', sideOffset = 6, ...props }, ref) {
    return (
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          className={cn('z-50 min-w-52 rounded-menu border border-border-subtle bg-surface-default p-1 shadow-overlay animate-fade-in', className)}
          {...props}
        />
      </MenuPrimitive.Portal>
    );
  },
);

const itemBase =
  'flex cursor-pointer select-none items-center gap-2 rounded-control px-3 py-2 text-body outline-none [&_svg]:size-4 [&_svg]:shrink-0 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50';

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<typeof MenuPrimitive.Item> {
  icon?: React.ReactNode;
  destructive?: boolean;
}

export const DropdownMenuItem = React.forwardRef<HTMLDivElement, DropdownMenuItemProps>(function DropdownMenuItem(
  { className, icon, destructive, children, ...props },
  ref,
) {
  return (
    <MenuPrimitive.Item
      ref={ref}
      className={cn(
        itemBase,
        destructive ? 'text-danger data-[highlighted]:bg-danger-bg' : 'text-text-primary data-[highlighted]:bg-surface-subtle',
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </MenuPrimitive.Item>
  );
});

export const DropdownMenuCheckboxItem = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof MenuPrimitive.CheckboxItem>>(
  function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
    return (
      <MenuPrimitive.CheckboxItem ref={ref} className={cn(itemBase, 'relative pl-8 text-text-primary data-[highlighted]:bg-surface-subtle', className)} {...props}>
        <MenuPrimitive.ItemIndicator className="absolute left-2.5 inline-flex">
          <Icon icon={Tick02Icon} aria-hidden className="text-brand-primary" />
        </MenuPrimitive.ItemIndicator>
        {children}
      </MenuPrimitive.CheckboxItem>
    );
  },
);

export function DropdownMenuSeparator({ className }: { className?: string }) {
  return <MenuPrimitive.Separator className={cn('my-1 h-px bg-border-subtle', className)} />;
}

export function DropdownMenuLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <MenuPrimitive.Label className={cn('px-3 py-1.5 text-meta font-medium text-text-secondary', className)}>{children}</MenuPrimitive.Label>;
}
